import {
  DocumentId,
  PreflightCoreApiClient,
  SubmissionInfo,
} from '../api/preflight-core-api-client';
import { DownloadState, PaymentStatus } from '../common-types';

export type PeerPilotEligibility = {
  eligibleForPreview?: boolean;
  eligibleForReport?: boolean;
  report?: boolean;
  preview?: boolean;
  mode?: 'DISABLED' | 'MANUAL' | 'AUTO';
};

export type AddOnService = {
  gjdPublisherName?: string;
  gjdJournalName?: string;
  addOnKey?: string;
  basePrice?: number;
  salePrice?: number;
  title?: string;
  addOnServiceId?: number;
  status?: 'PENDING' | 'COMPLETED';
  noOfImages?: number;
};

export type ExtendedSubmissionInfo =
  | {
      status: 'PROCESSING_FAILED';
      type: 'intermediate';
    }
  | {
      status: 'ERROR';
      type: 'intermediate';
    }
  | {
      status: 'REQUIRES_DOCUMENT';
      type: 'intermediate';
      finalPrice: string;
      currency: string;
      fileUploadDate?: string;
      tittle?: string;
      salePrice?: string;

      salePriceOriginal?: number;
      finalPriceOriginal?: number;
      discountPrice?: string;
      discountApplied?: boolean;
      orderId?: string;
      addOnServiceList?: AddOnService[];
      peerPilotEligibility?: PeerPilotEligibility | null;
    }
  | {
      status: 'REQUIRES_PAYMENT';
      paymentStatus: PaymentStatus;
      type: 'intermediate';
      editedFileName: string;
      finalPrice: string;
      currency: string;
      fileUploadDate?: string;
      tittle?: string;
      salePrice?: string;

      salePriceOriginal?: number;
      finalPriceOriginal?: number;
      discountPrice?: string;
      discountApplied?: boolean;
      orderId?: string;
      addOnServiceList?: AddOnService[];
      peerPilotEligibility?: PeerPilotEligibility | null;
    }
  | {
      status: PaymentStatus;
      type: 'final';
      downloadAvailableDays: number;
      downloadState: Exclude<DownloadState, 'EXPIRED' | 'NOT_AVAILABLE'>;
      editedFileName: string;
      fileUploadDate?: string;
      finalPrice?: string;
      currency?: string;
      tittle?: string;
      salePrice?: string;

      salePriceOriginal?: number;
      finalPriceOriginal?: number;
      discountPrice?: string;
      discountApplied?: boolean;
      orderId?: string;
      addOnServiceList?: AddOnService[];
      peerPilotEligibility?: PeerPilotEligibility | null;
    }
  | {
      status: 'NO_ISSUES';
      type: 'final';
      editedFileName?: string;
      tittle?: string;
    }
  | {
      status: 'EXPIRED';
      type: 'final';
      downloadAvailableDays: number;
      fileUploadDate?: string;
      tittle?: string;
    }
  | {
      status: 'SAMPLE';
      type: 'final';
      finalPrice: string;
      currency: string;
      fileUploadDate?: string;
      tittle?: string;
      salePrice?: string;

      salePriceOriginal?: number;
      finalPriceOriginal?: number;
      discountPrice?: string;
      discountApplied?: boolean;
      orderId?: string;
      addOnServiceList?: AddOnService[];
      peerPilotEligibility?: PeerPilotEligibility | null;
    };

type getExtendedSubmissionInfoType = {
  documentId: DocumentId;
  isSampleDocument: boolean;
  preflightCoreApiClient: PreflightCoreApiClient;
  currencyCode?: string;
};
export function getExtendedSubmissionInfo({
  documentId,
  isSampleDocument,
  preflightCoreApiClient,
  currencyCode,
}: getExtendedSubmissionInfoType): Promise<ExtendedSubmissionInfo> {
  return preflightCoreApiClient
    .getSubmissionInfo({ documentId }, currencyCode)
    .then((submissionInfo) =>
      submissionIntoToExtendedSubmissionInfo(submissionInfo, isSampleDocument),
    )
    .catch((error) => {
      console.error(error);

      return {
        status: 'ERROR',
        type: 'intermediate',
      };
    });
}

function submissionIntoToExtendedSubmissionInfo(
  submissionInfo: SubmissionInfo,
  isSampleDocument: boolean,
): ExtendedSubmissionInfo {
  if (submissionInfo.status === 'PROCESSING_FAILED') {
    return {
      status: 'PROCESSING_FAILED',
      type: 'intermediate',
    };
  }

  if (submissionInfo.status === 'REQUIRES_DOCUMENT') {
    return {
      status: 'REQUIRES_DOCUMENT',
      type: 'intermediate',
      finalPrice: submissionInfo.finalPrice,
      currency: submissionInfo.currency,
      fileUploadDate: submissionInfo.fileUploadDate,
      tittle: submissionInfo?.tittle ?? '',
      salePrice: submissionInfo?.salePrice ?? '',
      salePriceOriginal: submissionInfo.salePriceOriginal ?? 0,
      finalPriceOriginal: submissionInfo.finalPriceOriginal ?? 0,
      discountPrice: submissionInfo?.discountPrice ?? '',
      discountApplied: submissionInfo?.discountApplied ?? '',
      orderId: submissionInfo?.orderId ?? '',
      addOnServiceList: submissionInfo?.addOnServiceList ?? [],
      peerPilotEligibility: submissionInfo?.peerPilotEligibility ?? null,
    };
  }

  if (
    submissionInfo.status === 'REQUIRES_PAYMENT' &&
    submissionInfo.downloadState === 'EXPIRED'
  ) {
    return {
      status: 'EXPIRED',
      type: 'final',
      downloadAvailableDays: submissionInfo.downloadAvailableDays,
      fileUploadDate: submissionInfo.fileUploadDate,
      tittle: submissionInfo?.tittle ?? '',
    };
  }

  if (submissionInfo.status === 'REQUIRES_PAYMENT') {
    return {
      status: 'REQUIRES_PAYMENT',
      type: 'intermediate',
      editedFileName: submissionInfo.editedFileName,
      finalPrice: submissionInfo.finalPrice,
      paymentStatus: submissionInfo.paymentStatus,
      currency: submissionInfo.currency,
      fileUploadDate: submissionInfo.fileUploadDate,
      tittle: submissionInfo?.tittle ?? '',
      salePrice: submissionInfo?.salePrice ?? '',
      salePriceOriginal: submissionInfo.salePriceOriginal ?? 0,
      finalPriceOriginal: submissionInfo.finalPriceOriginal ?? 0,
      discountPrice: submissionInfo?.discountPrice ?? '',
      discountApplied: submissionInfo?.discountApplied ?? '',
      orderId: submissionInfo?.orderId ?? '',
      addOnServiceList: submissionInfo?.addOnServiceList ?? [],
      peerPilotEligibility: submissionInfo?.peerPilotEligibility ?? null,
    };
  }

  if (submissionInfo.downloadState === 'EXPIRED') {
    return {
      status: 'EXPIRED',
      type: 'final',
      downloadAvailableDays: submissionInfo.downloadAvailableDays,
      fileUploadDate: submissionInfo.fileUploadDate,
      tittle: submissionInfo?.tittle ?? '',
    };
  }

  if (isSampleDocument) {
    return {
      status: 'SAMPLE',
      type: 'final',
      finalPrice: submissionInfo.finalPrice,
      currency: submissionInfo.currency,
      fileUploadDate: submissionInfo.fileUploadDate,
      tittle: submissionInfo?.tittle ?? '',
      salePrice: submissionInfo?.salePrice ?? '',
      salePriceOriginal: submissionInfo.salePriceOriginal ?? 0,
      finalPriceOriginal: submissionInfo.finalPriceOriginal ?? 0,
      discountPrice: submissionInfo?.discountPrice ?? '',
      discountApplied: submissionInfo?.discountApplied ?? '',
      orderId: submissionInfo?.orderId ?? '',
      addOnServiceList: submissionInfo?.addOnServiceList ?? [],
      peerPilotEligibility: submissionInfo?.peerPilotEligibility ?? null,
    };
  }

  if (submissionInfo.downloadState === 'NOT_AVAILABLE') {
    return {
      status: 'NO_ISSUES',
      type: 'final',
      editedFileName: submissionInfo?.editedFileName ?? '',
      tittle: submissionInfo?.tittle ?? '',
    };
  }

  return {
    status: submissionInfo.paymentStatus,
    type: 'final',
    downloadAvailableDays: submissionInfo.downloadAvailableDays,
    downloadState: submissionInfo.downloadState,
    editedFileName: submissionInfo.editedFileName,
    fileUploadDate: submissionInfo.fileUploadDate,
    finalPrice: submissionInfo?.finalPrice ?? '',
    currency: submissionInfo?.currency ?? '',
    tittle: submissionInfo?.tittle ?? '',
    salePrice: submissionInfo?.salePrice ?? '',
    salePriceOriginal: submissionInfo.salePriceOriginal ?? 0,
    finalPriceOriginal: submissionInfo.finalPriceOriginal ?? 0,
    discountPrice: submissionInfo?.discountPrice ?? '',
    discountApplied: submissionInfo?.discountApplied ?? '',
    orderId: submissionInfo?.orderId ?? '',
    addOnServiceList: submissionInfo?.addOnServiceList ?? [],
    peerPilotEligibility: submissionInfo?.peerPilotEligibility ?? null,
  };
}
